import { useState, useEffect } from "react";
import { validateCarteGrise, uploadCarteGrise, type CarteGriseValidationResult } from "@services/api/tracking";

type Phase = "analyzing" | "result";

interface Props {
  file: File;
  ordreId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const TIPS = [
  { icon: "sun", text: "Bonne luminosité, évitez les zones sombres" },
  { icon: "frame", text: "Document entier visible, sans parties coupées" },
  { icon: "focus", text: "Image nette et bien mise au point" },
  { icon: "angle", text: "Prise de vue bien droite, sans angle" },
];

export default function CarteGriseUploadOverlay({ file, ordreId, onClose, onSuccess }: Props) {
  const [phase, setPhase] = useState<Phase>("analyzing");
  const [result, setResult] = useState<CarteGriseValidationResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Create preview
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Animate fake progress during analysis
  useEffect(() => {
    if (phase !== "analyzing") return;
    const interval = setInterval(() => {
      setProgress((p) => Math.min(p + Math.random() * 12, 90));
    }, 300);
    return () => clearInterval(interval);
  }, [phase]);

  // Run validation
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await validateCarteGrise(file, ordreId);
        if (cancelled) return;
        setProgress(100);
        setTimeout(() => {
          if (cancelled) return;
          setResult(res);
          setPhase("result");

          // Auto-upload if valid
          if (res.valid) {
            handleUpload();
          }
        }, 400);
      } catch {
        if (cancelled) return;
        // AI unavailable — proceed with upload
        setProgress(100);
        setTimeout(() => {
          if (cancelled) return;
          handleUpload();
        }, 300);
      }
    })();

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async () => {
    setUploading(true);
    try {
      await uploadCarteGrise(ordreId, file);
      setResult({ valid: true, is_carte_grise: true, is_readable: true, immatriculation_lue: null, immatriculation_attendue: null, immatriculation_match: null, message: "Carte grise envoyée avec succès." });
      setPhase("result");
      setTimeout(() => onSuccess(), 1200);
    } catch {
      setResult({ valid: false, is_carte_grise: true, is_readable: true, immatriculation_lue: null, immatriculation_attendue: null, immatriculation_match: null, message: "Une erreur est survenue lors de l'envoi. Veuillez réessayer." });
      setPhase("result");
    } finally {
      setUploading(false);
    }
  };

  const handleRetry = () => {
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-[env(safe-area-inset-top,12px)] pb-3">
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        <span className="text-[15px] font-semibold tracking-[-0.3px]">Carte grise</span>
        <div className="w-10" />
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-6 py-8 overflow-y-auto">
        {phase === "analyzing" && (
          <div className="flex flex-col items-center gap-8 w-full max-w-[320px] my-auto">
            {/* Preview thumbnail */}
            {previewUrl && (
              <div className="w-[200px] h-[130px] rounded-2xl overflow-hidden border border-neutral-200 shadow-sm">
                <img src={previewUrl} alt="" className="w-full h-full object-cover" />
              </div>
            )}

            {/* Progress */}
            <div className="w-full flex flex-col items-center gap-3">
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                <span className="text-[15px] font-medium tracking-[-0.2px]">Analyse en cours</span>
              </div>
              <div className="w-full h-[3px] bg-neutral-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-black rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <span className="text-[12px] text-neutral-400 font-medium">
                Vérification du document...
              </span>
            </div>
          </div>
        )}

        {phase === "result" && result && (
          <div className="flex flex-col items-center gap-8 w-full max-w-[360px]">
            {/* Status icon */}
            {result.valid ? (
              <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </div>
            )}

            {/* Title */}
            <div className="text-center">
              <h2 className="text-[22px] font-semibold tracking-[-0.5px] mb-2">
                {result.valid
                  ? uploading ? "Envoi en cours..." : "Document validé"
                  : !result.is_carte_grise
                    ? "Ce n'est pas une carte grise"
                    : result.immatriculation_match === false
                      ? "Mauvaise carte grise"
                      : "Immatriculation non lisible"
                }
              </h2>
              <p className="text-[14px] text-neutral-500 leading-[1.5] font-medium">
                {result.message}
              </p>
            </div>

            {/* Tips (show on failure) */}
            {!result.valid && result.immatriculation_match !== false && (
              <div className="w-full border border-neutral-200 rounded-2xl p-5 space-y-4">
                <span className="text-[13px] font-semibold tracking-[-0.2px] text-neutral-800">
                  Conseils pour une bonne photo
                </span>
                {TIPS.map((tip) => (
                  <div key={tip.icon} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0 mt-[-2px]">
                      <TipIcon type={tip.icon} />
                    </div>
                    <span className="text-[13px] text-neutral-600 leading-[1.4] font-medium pt-[4px]">
                      {tip.text}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Preview on failure */}
            {!result.valid && previewUrl && (
              <div className="w-full">
                <span className="text-[12px] font-medium text-neutral-400 mb-2 block">
                  Photo envoyée
                </span>
                <div className="w-full h-[140px] rounded-xl overflow-hidden border border-neutral-200">
                  <img src={previewUrl} alt="" className="w-full h-full object-cover opacity-60" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom action */}
      <div className="px-6 pb-[max(env(safe-area-inset-bottom,16px),16px)] pt-3">
        {phase === "result" && result && !result.valid && (
          <button
            onClick={handleRetry}
            className="w-full py-[16px] bg-black text-white font-semibold text-[15px] rounded-2xl hover:bg-neutral-800 transition-colors"
          >
            Reprendre la photo
          </button>
        )}
      </div>
    </div>
  );
}

function TipIcon({ type }: { type: string }) {
  const props = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "#525252", strokeWidth: 1.8, strokeLinecap: "round" as const, strokeLinejoin: "round" as const };

  switch (type) {
    case "sun":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      );
    case "frame":
      return (
        <svg {...props}>
          <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
        </svg>
      );
    case "focus":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4" />
        </svg>
      );
    case "angle":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M9 3v18" />
        </svg>
      );
    default:
      return null;
  }
}
